import { cn } from "@/lib/utils";
import { isBriquezi } from "@/const";
import { useTheme } from "@/contexts/ThemeContext";

/**
 * URLs dos logos oficiais da Briquezi Soluções (hospedados no storage do projeto).
 * Centralizados aqui para reaproveitamento em qualquer outro componente.
 */
export const SO_FACILITEI_ASSETS = {
  iconTransparent: "/client-storage/icone-transparente_cfc5760d.png?v=1",
  icon512: "/client-storage/icone-512_de786dda.webp?v=1",
  logoHorizontal: "/client-storage/logo-horizontal_d6804cdd.png?v=1",
  logoHorizontalLarge: "/client-storage/logo-horizontal-large_e5d93146.webp?v=1",
  logoHorizontalWhite: "/client-storage/logo-horizontal-branco_0e361cd2.png?v=1",
} as const;

export const BRIQUEZI_ASSETS = {
  iconTransparent: "/img-clientes/favicon_icone_briquezi.ico?v=1",
  icon512: "/img-clientes/favicon_icone_briquezi.ico?v=1",
  logoHorizontal: "/img-clientes/Briquezi Solucoes.webp?v=1",
  logoHorizontalLarge: "/img-clientes/Briquezi Solucoes.webp?v=1",
  logoHorizontalWhite: "/img-clientes/Briquezi-Solucoes-branco.webp?v=1",
} as const;

type LogoVariant = "horizontal" | "horizontal-white" | "icon" | "icon-large";

type Props = {
  className?: string;
  /** Forma do logo desejada. */
  variant?: LogoVariant;
  /** Altura em pixels do logo (mantém proporção). */
  height?: number;
  /** Texto alternativo customizado. */
  alt?: string;
};

/**
 * Renderiza os logos oficiais de forma dinâmica (Multi-Tenant & White-Label).
 */
export function SoFaciliteiLogo({
  className,
  variant = "horizontal",
  height = 40,
  alt,
}: Props) {
  let effectiveLogo: string | null = null;
  let effectiveAlt: string = isBriquezi ? "Briquezi emissor Fiscal" : "Briquezi Soluções Contabilidade ágil e fácil";

  try {
    const { effectiveLogoUrl, effectiveBrandName } = useTheme();
    if (effectiveLogoUrl) {
      effectiveLogo = effectiveLogoUrl;
    }
    if (effectiveBrandName) {
      effectiveAlt = effectiveBrandName;
    }
  } catch {}

  const assets = isBriquezi ? BRIQUEZI_ASSETS : SO_FACILITEI_ASSETS;
  
  const sources: Record<LogoVariant, string> = {
    horizontal: assets.logoHorizontalLarge,
    "horizontal-white": assets.logoHorizontalWhite,
    icon: assets.iconTransparent,
    "icon-large": assets.icon512,
  };

  // Se a variante solicitada for 'horizontal-white', utiliza estritamente a versão branca correspondente
  // Para a Briquezi, garante o logo oficial branco do rodapé da briquezi.com.br
  let src = sources[variant];
  if (variant === "horizontal-white") {
    src = assets.logoHorizontalWhite;
  } else if (isBriquezi) {
    // Para Briquezi, se o logo configurado no banco for um upload antigo desatualizado ("web solution"),
    // garante os assets oficiais da marca
    if (effectiveLogo && !effectiveLogo.includes("17870") && !effectiveLogo.toLowerCase().includes("web")) {
      src = effectiveLogo;
    } else {
      src = sources[variant];
    }
  } else if (effectiveLogo) {
    src = effectiveLogo;
  }

  const isIcon = variant === "icon" || variant === "icon-large";

  return (
    <img
      src={src}
      alt={alt || effectiveAlt}
      className={cn("block select-none object-contain", isIcon ? "rounded" : "", className)}
      style={{ height, width: "auto" }}
      draggable={false}
      onError={(e) => {
        if (e.currentTarget.src !== sources[variant]) {
          e.currentTarget.src = sources[variant];
        }
      }}
    />
  );
}
